import React, { useMemo, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import styles from './index.module.scss';
import { PlaceholderIconSvg } from './../../svgs/PlaceholderIconSvg';
import { useAttachCustomerToCart } from '../../../integrations/react_query/mutations';
import { CustomerPayload } from '../../../integrations/react_query/queryFn';

interface INameIndex {
	list: any;
	styleClass?: string;
	closeModal: () => void;
}

export const VirtualizedNameIndexer = ({ list, styleClass, closeModal }: INameIndex) => {
	const { mutateAsync } = useAttachCustomerToCart();
	const parentRef = useRef<HTMLDivElement>(null);

	const flatList = useMemo(() => {
		const rows: { type: 'header' | 'customer'; data: any }[] = [];
		Object.entries(list).forEach(([nameIndex, valuesIndex]: any) => {
			rows.push({ type: 'header', data: nameIndex });
			valuesIndex.forEach((value: any) => {
				rows.push({ type: 'customer', data: value });
			});
		});
		return rows;
	}, [list]);

	const rowVirtualizer = useVirtualizer({
		count: flatList.length,
		getScrollElement: () => parentRef.current,
		estimateSize: () => 60,
		overscan: 5,
		measureElement: (el) => el.getBoundingClientRect().height,
	});

	const handleAttachCustomerToCart = ({
		firstName,
		lastName,
		id,
		email,
		phoneNumber,
		birthday,
		organizationName,
	}: CustomerPayload) => {
		mutateAsync({ firstName, lastName, id, email, phoneNumber, birthday, organizationName });
		closeModal();
	};

	return (
		<div ref={parentRef} className={`overflow-auto ${styles.innerWrapper} ${styleClass || ''}`}>
			<div
				style={{
					height: `${rowVirtualizer.getTotalSize()}px`,
					position: 'relative',
					width: '100%',
					boxSizing: 'border-box',
				}}
			>
				{rowVirtualizer.getVirtualItems().map((virtualRow) => {
					const row = flatList[virtualRow.index];
					return (
						<div
							key={virtualRow.key}
							ref={rowVirtualizer.measureElement}
							style={{
								position: 'absolute',
								top: 0,
								left: 0,
								width: '100%',
								transform: `translateY(${virtualRow.start}px)`,
							}}
						>
							{row.type === 'header' ? (
								<div className={`${styles.nameIndex} text-uppercase`}>{row.data}</div>
							) : (
								<div
									onClick={() =>
										handleAttachCustomerToCart({
											firstName: row.data.firstName,
											lastName: row.data.lastName,
											id: row.data._id,
											email: row.data.email,
											phoneNumber: row.data.phoneNumber,
											birthday: row.data.birthday,
											organizationName: row.data?.organizationName,
										})
									}
									className={`${styles.valueIndex} d-flex align-items-center justify-content-between`}
								>
									<div className="d-flex align-items-center">
										<PlaceholderIconSvg width="35" height="35" viewBox="0 0 50 50" />
										<div className="mx-2">
											<span className="font-bold">
												{row.data.customerType === 'Organization' && row.data.organizationName}
											</span>

											<div>
												{row.data.firstName} {row.data.lastName}
											</div>
										</div>
									</div>
									<div>{row.data?.phoneNumber}</div>
								</div>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
};

export default VirtualizedNameIndexer;
